import { useEffect, useRef, useContext } from 'react';
import { CONTEXTS } from 'figshelf-modules';

/**
 * Reads the logged-in user's premium colorPalette settings and applies the
 * primary colour to the global MUI theme when paletteScope === 'global'.
 * Uses stable primitive deps to avoid spurious re-fires.
 */
const PRE_PREMIUM_KEY = 'theme_pre_premium_color';

const usePremiumPaletteSync = () => {
  const auth = useContext(CONTEXTS.AuthContext);
  const { changePrimaryColor, primaryColor } = CONTEXTS.useTheme();

  // Track current primaryColor via ref so we can read it inside the effect
  // without making it a dependency (avoids re-fire loop after color changes).
  const primaryColorRef = useRef(primaryColor);
  useEffect(() => {
    primaryColorRef.current = primaryColor;
  });

  // Stable primitive values for deps
  const isPremium = !!(auth?.userModel?.isPremium ?? auth?.user?.isPremium);
  const paletteScope =
    auth?.userModel?.premiumSettings?.paletteScope ??
    auth?.user?.premiumSettings?.paletteScope;
  const paletteColor =
    auth?.userModel?.premiumSettings?.colorPalette?.config?.primary ??
    auth?.user?.premiumSettings?.colorPalette?.config?.primary;

  useEffect(() => {
    const shouldApplyGlobal =
      isPremium &&
      paletteScope === 'global' &&
      paletteColor &&
      typeof paletteColor === 'string';

    if (shouldApplyGlobal) {
      // Save the pre-premium colour only on first application
      if (!localStorage.getItem(PRE_PREMIUM_KEY)) {
        localStorage.setItem(PRE_PREMIUM_KEY, primaryColorRef.current || '#49A0AD');
      }
      changePrimaryColor(paletteColor);
    } else {
      // Restore the saved pre-premium colour when global scope is inactive
      const saved = localStorage.getItem(PRE_PREMIUM_KEY);
      if (saved) {
        localStorage.removeItem(PRE_PREMIUM_KEY);
        changePrimaryColor(saved);
      }
    }
  }, [isPremium, paletteScope, paletteColor, changePrimaryColor]);
};

export default usePremiumPaletteSync;
